import { useState } from 'react';
import { getRooms, getSubjectsList } from 'services/api';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import { educationParallelTextRepresentation } from 'entities/education-class';
import { useGetEducationClassListQuery } from 'shared/api';
import type { AppSelectOption } from 'shared/UI/UIKit/Select';
import type { TeacherWorkload } from 'shared/api';
import { teacherWorkloadsFilterFields } from '../config';
import { useTableService } from './use-table-service';

export const useTeacherWorkloads = () => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [selectedWorkload, setSelectedWorkload] = useState<TeacherWorkload | null>(null);
  const [siderVisible, setSiderVisible] = useState(false);
  const [filterFields, setFilterFields] = useState(teacherWorkloadsFilterFields);

  const { data: educationClasses } = useGetEducationClassListQuery({ size: 1000 });

  const filterFieldsWithOptions = filterFields.map((field) =>
    field.name === 'educationClassIds'
      ? {
          ...field,
          options:
            educationClasses?.content.map((ec) => ({
              label: `${educationParallelTextRepresentation[ec.parallel]}${ec.litera} класс`,
              value: ec.id,
            })) || [],
        }
      : field,
  );

  const showCreateModal = () => {
    setCreateModalVisible(true);
  };

  const hideCreateModal = () => {
    setCreateModalVisible(false);
  };

  const onEdit = (record: TeacherWorkload) => {
    setSelectedWorkload(record);
    setEditModalVisible(true);
  };

  const hideEditModal = () => {
    setEditModalVisible(false);
  };

  const onDelete = (record: TeacherWorkload) => {
    setSelectedWorkload(record);
    setRemoveModalVisible(true);
  };

  const hideDeleteModal = () => {
    setRemoveModalVisible(false);
  };

  const onCopy = (record: TeacherWorkload) => {
    setSelectedWorkload(record);
    setCopyModalVisible(true);
  };

  const hideCopyModal = () => {
    setCopyModalVisible(false);
    setSelectedWorkload(null);
  };

  const { state: tableServiceState, methods: tableMServiceMethods } = useTableService({
    onEdit,
    onDelete,
    onCopy,
  });

  const showSider = () => {
    setSiderVisible(true);
  };

  const hideSider = () => {
    setSiderVisible(false);
  };

  const onLoadFilterOptions = async (fieldName: string) => {
    const loadingIndex = filterFields.findIndex((field) => field.name === fieldName);

    if (loadingIndex < 0) {
      return;
    }

    let options: AppSelectOption[] = [];

    setFilterFields((prev) => {
      const newState = [...prev];
      newState[loadingIndex].loading = true;
      return newState;
    });

    switch (fieldName) {
      case 'preferredRoomIds': {
        const response = await getRooms();
        options = response.content.map((room) => ({
          label: `${room.number} - ${room.name}`,
          value: room.id,
        }));
        break;
      }
      case 'subjectIds': {
        const response = await getSubjectsList();
        options = response.content.map((subject) => ({
          label: formatSubjectWithLevel(subject.name, subject.educationLevel),
          value: subject.id,
        }));
        break;
      }
      default:
        return;
    }
    setFilterFields((prev) => {
      const newState = [...prev];
      newState[loadingIndex].options = options;
      newState[loadingIndex].loading = false;
      return newState;
    });
  };

  return {
    state: {
      ...tableServiceState,
      createModalVisible,
      editModalVisible,
      removeModalVisible,
      selectedWorkload,
      copyModalVisible,
      siderVisible,
      filterFields: filterFieldsWithOptions,
    },
    methods: {
      ...tableMServiceMethods,
      showCreateModal,
      hideCreateModal,
      hideEditModal,
      hideDeleteModal,
      hideCopyModal,
      showSider,
      hideSider,
      onLoadFilterOptions,
    },
  };
};
